import React, { useContext, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faSquare, faSquareCheck } from '@fortawesome/free-regular-svg-icons';

import CommissionDetails from 'app/src/components/Orders/CommissionDetails';
import config from 'app/src/config';
import { ClientContext } from 'app/src/contexts/ClientContext';
import { Button } from 'app/src/elements/buttons';
import EditableDetails from 'app/src/elements/EditableDetails';
import { StyledTextInput } from 'app/src/elements/inputs';
import Modal from 'app/src/elements/Modal';
import { Text, Subtitle } from 'app/src/styles';
import { cityStateZip } from 'app/src/utils/address';

import Licenses from './Licenses';
import { DEALER_FOR_REVIEW, REVIEW_DEALER } from './queries';
import Users from './Users';

const DetailContainer = styled(View)`
  ${props => (props.isMatch && !props.isMobile) ? `
    border-left-width: 1px;
    border-left-color: gray;
    border-left-style: solid;
    padding-left: 10px;
  ` : ''
  }

  ${props => props.isMobile ? `
    border-top-width: 1px;
    border-top-color: black;
    border-top-style: solid;
    margin-top: 10px;
  ` : ''
  }

  margin-right: 10px;
`;

const ChooseName = ({ dealer, pendingName, setShow, submit }) => {
  const [selectedName, setSelectedName] = useState();
  const [selectedOption, setSelectedOption] = useState();

  const handleSubmit = () => {
    submit('Approved', false, dealer.id, selectedName);
  };

  const SelectName = ({ option, name }) => {
    return (
      <Pressable
        onPress={() => {
          setSelectedOption(option);
          if (name) { setSelectedName(name); }
        }}
      >
        {option === selectedOption ? (
          <FontAwesomeIcon icon={faSquareCheck} color="green" size={20} />
        ) : (
          <FontAwesomeIcon icon={faSquare}  size={20} />
        )}
      </Pressable>
    );
  };

  const readyToSubmit = selectedName && selectedOption;
  const buttonLabel = readyToSubmit ?
    `Use "${selectedName}" as name and complete merge` :
    'Choose a name to complete merge';

  return (
    <Modal title="Choose Name" setShow={setShow}>
      <Text>
        The dealer submitted a different name than what is currently
        recorded in OklahomaTempTag.com.
      </Text>

      <Text>
        Which name should be used on future tag orders?
      </Text>

      <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
        <SelectName option="pending" name={pendingName} />
        <Text style={{ fontWeight: 600 }}>{pendingName}</Text>
        <Text>(Submitted by Dealer)</Text>
      </View>

      <View style={{ flexDirection: 'row', gap: 8 }}>
        <SelectName option="current" name={dealer.name} />
        <Text style={{ fontWeight: 600 }}>{dealer.name}</Text>
        <Text>(Currently in system)</Text>
      </View>

      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
        <SelectName option="custom" />
        <Text>Custom</Text>

        {'custom' === selectedOption && (
          <StyledTextInput
            value={selectedName}
            onChangeText={value => setSelectedName(value)}
            style={{ flex: 1 }}
          />
        )}
      </View>

      <Button
        onPress={handleSubmit}
        label={buttonLabel}
        disabled={!readyToSubmit}
        wide
      />
    </Modal>
  );
};

const DealerDetails = props => {
  const { dealer, pendingName, isMatch, isMobile, submit, refetch } = props;
  const [showChooseName, setShowChooseName] = useState();

  const compareNames = () => {
    if (pendingName.toLowerCase() !== dealer.name.toLowerCase()) {
      setShowChooseName(true);
    } else {
      submit('Approved', false, dealer.id);
    }
  };

  return (
    <DetailContainer isMatch={isMatch} isMobile={isMobile}>
      <Subtitle>{isMatch ? 'Match' : 'Submitted'}</Subtitle>
      <Subtitle>{dealer.name}</Subtitle>

      <View style={{ flexDirection: 'row' }}>
        <View style={{ flex: 1}}>
          {dealer.licenses.map(license => (
            <Text key={license.id}>
              License: {license.licenseType} {license.licenseNumber}
            </Text>
          ))}
        </View>

        <View style={{ marginLeft: 10 }}>
          <EditableDetails
            modalContent={Licenses}
            title="Dealer Licenses"
            id={dealer.id}
            canEdit={!isMatch}
            refetch={refetch}
          />
        </View>
      </View>

      <Text style={{ marginTop: 10 }}>{dealer.street}</Text>
      <Text>{cityStateZip(dealer)}</Text>

      <Text style={{ marginTop: 10 }}>Phone: {dealer.phone}</Text>

      <View style={{ marginTop: 10 }}>
        <Users dealer={dealer} />
      </View>

      {isMatch ? (
        <Button
          label="Approve and Merge"
          onPress={compareNames}
        />
      ) : (
        <Button
          label="Approve"
          onPress={() => submit('Approved')}
        />
      )}

      {showChooseName && (
        <ChooseName
          dealer={dealer}
          pendingName={pendingName}
          setShow={setShowChooseName}
          submit={submit}
        />
      )}
    </DetailContainer>
  );
};

const Review = ({ dealer, setShow, refetch }) => {
  const { isMobile } = useContext(ClientContext);

  const { data, refetch: refetchDealer } = useQuery(DEALER_FOR_REVIEW, {
    variables: { id: dealer.id }
  });

  const [reviewDealer] = useMutation(REVIEW_DEALER, {
    onCompleted: () => {
      setShow(false);
      refetch();
    }
  });

  const submit = (approvalStatus, createSampleOrders, mergeIntoId, name) => {
    let confirmation = `Change status to ${approvalStatus}?`;

    if (
      'Approved' === approvalStatus &&
      !mergeIntoId &&
      data.dealer.matches.length > 0
    ) {
      confirmation = confirmation +
        " PLEASE CONFIRM THAT YOU DON'T WANT TO MERGE INTO A MATCHING DEALER";
    }

    if (window.confirm(confirmation)) {
      reviewDealer({
        variables: {
          id: dealer.id,
          mergeIntoId,
          approvalStatus,
          createSampleOrders: !!createSampleOrders,
          name: name,
        }
      });
    }
  };

  return (
    <Modal title="Review Pending Dealer" setShow={setShow}>
      {data?.dealer.matches.length > 0 && (
        <View style={{ borderBottomWidth: 1 }}>
          <Text style={{ fontWeight: 600 }}>
            Found {data?.dealer.matches.length} dealer(s) that might be a match.
            If the match looks right, go to it and choose "Approve and Merge".
          </Text>
        </View>
      )}

      <View style={isMobile ? {} : { flexDirection: 'row', gap: 10 }}>
        {data?.dealer && (
          <View>
            <DealerDetails
              dealer={data.dealer}
              submit={submit}
              refetch={() => { refetch(); refetchDealer(); }}
            />

            <Button
              label="Reject"
              onPress={() => submit('Rejected')}
              type="Cancel"
            />
          </View>
        )}

        <View style={{ marginTop: 10 }}>
          <CommissionDetails dealer={data?.dealer} />
        </View>

        {data?.dealer.matches.map(match => (
          <DealerDetails
            key={match.id}
            dealer={match}
            submit={submit}
            isMobile={isMobile}
            pendingName={data?.dealer?.name}
            isMatch
          />
        ))}
      </View>

      {config.app.isDemo && (
        <Button
          label="Approve and Create Sample Orders"
          onPress={() => submit('Approved', true)}
          wide
        />
      )}
    </Modal>
  );
};

export default Review;
